import { CopyOutlined, DeleteOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Col, Input, Row, Select } from "antd";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { ConnectionSequenceStepMeaningType, RequestMethod, RequestPayloadType } from "types/enums";
import { DrawerId } from "types/models";

import { ConnectionSequenceStepFormData, FormData } from "./formSchema";

const { Option } = Select;

import ConnectionSequenceStepMeaningData = App.Data.ConnectionSequenceStepMeaningData;
interface StepFormProps {
    handleRemoveMethod: () => void;
    name: number;
    disabled: boolean;
}

export const StepForm: FC<StepFormProps> = ({ handleRemoveMethod, name, disabled }) => {
    const { t } = usePaldockTranslation("ui.connection-creator-editor.step");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const stepSetting = DrawerId.MethodStepSetting;
    const generalFormUtils = useGeneralForm<FormData>();
    const { watch, control, buildArrayFieldNames } = generalFormUtils;

    const methods = watch("methods");
    const currentMethod = methods?.[name];

    const methodFields = buildArrayFieldNames<ConnectionSequenceStepFormData>("methods", name);

    return (
        <Row gutter={16} align="middle" className="flex flex-wrap w-full">
            <Col xs={24} sm={12} md={6} lg={3}>
                <FormItem control={control} name={`methods.${name}.name`} label={tFields("name.label")} required disabled={disabled}>
                    <Input placeholder={tFields("name.placeholder")} />
                </FormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={3} xl={4}>
                <FormItem
                    control={control}
                    name={`methods.${name}.requestConfig.method`}
                    label={t("fields.method.label")}
                    required
                    disabled={disabled}
                >
                    <Select>
                        {Object.values(RequestMethod).map((methodValue) => (
                            <Option value={methodValue} key={methodValue}>
                                {methodValue}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            <Col xs={24} sm={12} md={8} lg={3} xl={4}>
                <FormItem
                    control={control}
                    name={methodFields.meanings?.name || `methods.${name}.meanings`}
                    label={t("fields.meaning.label")}
                    disabled={disabled}
                    getValueProps={(value) => {
                        const typeArray =
                            value?.map((meaning: ConnectionSequenceStepMeaningData) => meaning?.type ?? meaning) ?? [];
                        return { value: typeArray };
                    }}
                    getValueFromEvent={(selectedTypes: ConnectionSequenceStepMeaningType[]) => {
                        const currentMeanings = currentMethod?.meanings || [];
                        return selectedTypes.map((type) => {
                            const existingMeaning = currentMeanings.find(
                                (m: { type: ConnectionSequenceStepMeaningType }) => m.type === type
                            );
                            return (
                                existingMeaning || {
                                    id: null,
                                    connection_sequence_step_id: null,
                                    type,
                                }
                            );
                        });
                    }}
                >
                    <Select
                        mode="tags"
                        placeholder={t("fields.meaning.placeholder")}
                        popupMatchSelectWidth={false}
                        popupClassName="min-w-[100px] w-auto"
                    >
                        {[
                            ConnectionSequenceStepMeaningType.Check,
                            ConnectionSequenceStepMeaningType.Send,
                            ConnectionSequenceStepMeaningType.PostSendVerification,
                        ].map((stepMeaning) => (
                            <Option value={stepMeaning} key={stepMeaning}>
                                {t(`fields.meaning.options.${stepMeaning}`)}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            <Col xs={24} sm={24} md={12} lg={6}>
                <FormItem
                    control={control}
                    name={`methods.${name}.requestConfig.url`}
                    label={t("fields.url.label")}
                    required
                    disabled={disabled}
                >
                    <Input placeholder={t("fields.url.placeholder")} />
                </FormItem>
            </Col>

            <Col xs={24} sm={12} md={6} lg={3}>
                <FormItem
                    control={control}
                    name={`methods.${name}.requestConfig.send_payload_as`}
                    label={t("fields.send_payload_as.label")}
                    required
                    disabled={disabled}
                >
                    <Select>
                        {Object.values(RequestPayloadType).map((payload) => (
                            <Option value={payload} key={payload}>
                                {payload}
                            </Option>
                        ))}
                    </Select>
                </FormItem>
            </Col>

            <Col>
                <Button icon={<CopyOutlined />} disabled={disabled} />
            </Col>
            <Col>
                <DrawerLink
                    drawerId={stepSetting}
                    search={{ [`${stepSetting}-id`]: name.toString() }}
                    action="show"
                    disabled={disabled}
                >
                    <Button icon={<SettingOutlined />} disabled={disabled} />
                </DrawerLink>
            </Col>
            <Col>
                <Button danger icon={<DeleteOutlined />} onClick={handleRemoveMethod} disabled={disabled} />
            </Col>
        </Row>
    );
};
